/**
 * Field-level confidence badges for AI Lead Intake HITL.
 */

/**
 * @typedef {'high'|'needs_confirmation'|'not_found'|'optional'|'manual'} FieldConfidenceLevel
 */

/**
 * Derive per-field confidence from draft autofill + current values.
 * @param {object} params
 * @param {object} params.fields — current HITL fields
 * @param {object|null} [params.draft]
 * @param {string[]} [params.confirmedFields] — staff-confirmed field keys
 * @param {number} [params.minConfidenceToHighlight=0.5] — system setting threshold
 * @returns {Record<string, { level: FieldConfidenceLevel, label: string }>}
 */
export function deriveFieldConfidenceMap({
  fields = {},
  draft = null,
  confirmedFields = [],
  minConfidenceToHighlight = 0.5,
} = {}) {
  const suggestions = draft?.autofill?.suggestions || {};
  const confirmed = new Set(confirmedFields);
  const threshold = Number(minConfidenceToHighlight);
  const overallConfidence = Number(draft?.confidence);
  const belowHighlightThreshold =
    Number.isFinite(overallConfidence) &&
    Number.isFinite(threshold) &&
    overallConfidence < threshold;
  const keys = [
    "name",
    "phone",
    "email",
    "address",
    "moving_from",
    "moving_to",
    "move_date",
    "serviceType",
    "distance_km",
    "itemsText",
  ];

  /** @type {Record<string, { level: FieldConfidenceLevel, label: string }>} */
  const map = {};

  for (const key of keys) {
    if (confirmed.has(key)) {
      map[key] = { level: "high", label: "Confirmed" };
      continue;
    }

    const value = String(fields[key] ?? "").trim();
    const optional = key === "email" || key === "address" || key === "distance_km";

    if (!value) {
      map[key] = optional
        ? { level: "optional", label: "Optional" }
        : { level: "not_found", label: "Not found" };
      continue;
    }

    // Heuristic: move_date / distance often need human confirm
    if (key === "move_date" || key === "distance_km") {
      const suggestedKm = suggestions.suggestedRouteKm;
      if (
        key === "distance_km" &&
        suggestedKm != null &&
        String(value) === String(suggestedKm)
      ) {
        map[key] = { level: "needs_confirmation", label: "Needs confirmation" };
        continue;
      }
      if (key === "move_date") {
        map[key] = { level: "needs_confirmation", label: "Needs confirmation" };
        continue;
      }
    }

    const wasAutofilled =
      Boolean(draft?.autofill?.enabled) &&
      draft?.autofill?.appliedAt &&
      (Boolean(suggestions.hits) || Boolean(draft.confidence));

    if (wasAutofilled) {
      if (belowHighlightThreshold) {
        map[key] = { level: "needs_confirmation", label: "Low confidence" };
      } else {
        map[key] = { level: "high", label: "AI suggested" };
      }
    } else {
      map[key] = { level: "manual", label: "Entered" };
    }
  }

  return map;
}

/**
 * Build confirmation checklist items that soft-gate Create Lead.
 * @param {object} params
 * @returns {Array<{ id: string, field?: string, title: string, detail: string, action: string, severity: 'warning'|'error' }>}
 */
export function buildConfirmationChecklistItems({
  fields = {},
  draft = null,
  fieldConfidence = {},
  accessIncomplete = false,
  confirmedFields = [],
} = {}) {
  const confirmed = new Set(confirmedFields);
  const items = [];

  const pushIf = (cond, item) => {
    if (cond) items.push(item);
  };

  pushIf(!String(fields.name || "").trim(), {
    id: "name",
    field: "name",
    title: "Customer name",
    detail: "Required before Create Lead",
    action: "Add",
    severity: "error",
  });

  pushIf(!String(fields.phone || "").trim(), {
    id: "phone",
    field: "phone",
    title: "Phone number",
    detail: "Required before Create Lead",
    action: "Add",
    severity: "error",
  });

  pushIf(
    !confirmed.has("move_date") &&
      (fieldConfidence.move_date?.level === "needs_confirmation" ||
        !String(fields.move_date || "").trim()),
    {
      id: "move_date",
      field: "move_date",
      title: "Move date",
      detail: String(fields.move_date || "").trim() || "Not provided",
      action: String(fields.move_date || "").trim() ? "Confirm" : "Add",
      severity: "warning",
    }
  );

  pushIf(accessIncomplete, {
    id: "access",
    field: null,
    title: "Access information",
    detail: "Present/Absent not confirmed for all access rows",
    action: "Review",
    severity: "error",
  });

  pushIf(
    !confirmed.has("distance_km") &&
      Boolean(String(fields.distance_km || "").trim()) &&
      fieldConfidence.distance_km?.level === "needs_confirmation",
    {
      id: "distance_km",
      field: "distance_km",
      title: "Route distance",
      detail: String(fields.distance_km || "").trim(),
      action: "Confirm",
      severity: "warning",
    }
  );

  pushIf(!String(fields.moving_from || "").trim() || !String(fields.moving_to || "").trim(), {
    id: "route",
    field: !String(fields.moving_from || "").trim() ? "moving_from" : "moving_to",
    title: "Route addresses",
    detail: "From/To incomplete",
    action: "Add",
    severity: "warning",
  });

  const hasInventory =
    (Array.isArray(fields.inventoryRows) &&
      fields.inventoryRows.some((r) => String(r?.name || "").trim())) ||
    Boolean(String(fields.itemsText || "").trim());
  pushIf(!hasInventory, {
    id: "inventory",
    field: "itemsText",
    title: "Inventory",
    detail: "No items listed",
    action: "Add",
    severity: "warning",
  });

  // Unresolved customer match conflicts — hard-block Accept
  const conflicts = draft?.customerMatch?.fieldConflicts;
  if (
    Array.isArray(conflicts) &&
    conflicts.length > 0 &&
    !draft?.customerMatch?.resolution
  ) {
    items.push({
      id: "customer_conflict",
      field: null,
      title: "Customer match conflict",
      detail: `${conflicts.length} field(s) differ from CRM — pick Use Customer or Use AI`,
      action: "Resolve",
      severity: "error",
    });
  }

  if (draft?.customerMatch?.status === "ambiguous") {
    items.push({
      id: "customer_ambiguous",
      field: null,
      title: "Ambiguous customer match",
      detail: "Phone and email match different customers — pick one or unlink",
      action: "Resolve",
      severity: "error",
    });
  }

  return items;
}

/**
 * Soft-block Create Lead when checklist has warning items (override allowed).
 * Hard-block (error severity) cannot be overridden — includes access, required
 * fields, and unresolved/ambiguous customer match.
 * @param {Array} items
 * @returns {{ blocked: boolean, hardBlocked: boolean, softBlocked: boolean, warningCount: number }}
 */
export function evaluateConfirmationGate(items = []) {
  const list = Array.isArray(items) ? items : [];
  const hardBlocked = list.some((i) => i.severity === "error");
  const warningCount = list.filter((i) => i.severity === "warning").length;
  return {
    blocked: list.length > 0,
    hardBlocked,
    softBlocked: !hardBlocked && warningCount > 0,
    warningCount,
  };
}

export const INTAKE_WIZARD_STEPS = Object.freeze([
  {
    id: "source",
    label: "Source",
    shortLabel: "Source",
    mobileOnly: true,
  },
  {
    id: "customer_route",
    label: "Customer & Route",
    shortLabel: "Customer",
    desktopLabel: "Customer & Route",
  },
  {
    id: "inventory",
    label: "Inventory",
    shortLabel: "Inventory",
    desktopLabel: "Inventory",
  },
  {
    id: "access_services",
    label: "Access & Services",
    shortLabel: "Access",
    desktopLabel: "Access & Services",
  },
  {
    id: "review",
    label: "Review & Create",
    shortLabel: "Review",
    desktopLabel: "Review & Create",
  },
]);

/**
 * Desktop step rail excludes Source (source is sticky panel).
 */
export function getDesktopWizardSteps() {
  return INTAKE_WIZARD_STEPS.filter((s) => !s.mobileOnly);
}

/**
 * Mobile linear steps include Source first.
 */
export function getMobileWizardSteps() {
  return [...INTAKE_WIZARD_STEPS];
}
